import type { components } from '@/api/generated/schema';

type Job = components['schemas']['JobResponse'];
type JobVisit = components['schemas']['JobVisitResponse'];

/**
 * One card on the dispatch board (M2-52, pass B2b): a VISIT of a job, or a
 * job with no visits at all (which lives in the rail until it is placed).
 *
 * Kyle chose a card per visit (2026-09-25): a three-day repipe is three cards,
 * each under the technician booked that day, and dragging one moves that day
 * alone. So the board's unit is no longer the job.
 *
 * <b>Shaped as a job, deliberately.</b> Every field of the job is carried, with
 * the VISIT's time and technician in `scheduledStart`/`scheduledEnd`/
 * `assignedUserId`/`assignedUserName`, and `id` is the card's own key — the
 * visit's id, or the job's for an unscheduled one. That is what lets the
 * board's arithmetic (`dispatchBoard.ts`) and the conflict rules
 * (`conflicts.ts`) work on cards unchanged: both only ever read an id, a
 * technician and a start/end. Anything that needs the JOB — the API, the
 * detail page's route — uses `jobId`, never `id`.
 */
export interface BoardItem extends Job {
  /** The job this card belongs to. `id` is the CARD's key; this is the job's. */
  jobId: string;
  /**
   * The visit this card is, or null for a job that has none yet — which
   * includes one whose only visit is still the optimistic placement's (see
   * `itemFor`): either way its next move goes through `/schedule`.
   */
  visitId: string | null;
  /** 1-based position among the job's visits, in start order (1 when it has none). */
  visitNumber: number;
  /** How many visits the job has (0 when it has none). */
  visitCount: number;
  /**
   * M2-21: the newest appointment email about this visit bounced or was
   * dropped (the visit's `emailBounced`, the office's only — false for anyone
   * else, and for a job with no visits, which has had no appointment emails).
   */
  emailBounced: boolean;
}

/**
 * Every job's cards: one per visit, or one for a job with no visits. Visits
 * arrive in start order from the API (`JobResponse.visits`), which is the
 * order "Visit 2 of 3" counts in.
 */
export function boardItems(jobs: readonly Job[]): BoardItem[] {
  return jobs.flatMap((job) =>
    job.visits.length === 0
      ? [{ ...job, jobId: job.id, visitId: null, visitNumber: 1, visitCount: 0, emailBounced: false }]
      : job.visits.map((visit, index) => itemFor(job, visit, index)),
  );
}

/**
 * How many JOBS some cards belong to — what every "3 jobs" on the board
 * counts (the rail's heading, a technician's column, the conflict summary).
 * A card is a visit since M2-52, so counting cards made one three-day repipe
 * read "3 jobs" in its technician's week row.
 */
export function jobCount(items: readonly Pick<BoardItem, 'jobId'>[]): number {
  return new Set(items.map((item) => item.jobId)).size;
}

const PENDING_VISIT_PREFIX = 'pending-';

/**
 * The id the optimistic update gives the visit `/schedule` is about to create
 * (`usePlaceJob`'s `placed`), until the refetch brings the real one.
 */
export function pendingVisitId(jobId: string): string {
  return `${PENDING_VISIT_PREFIX}${jobId}`;
}

function itemFor(job: Job, visit: JobVisit, index: number): BoardItem {
  // ⚠️ A visit the optimistic `/schedule` placement invented has no id the
  // server knows: `PUT`/`DELETE …/visits/pending-…` is a 404 (the route wants a
  // GUID), so dragging the card again before the refetch landed lost the move
  // and said "Couldn't move". Until then it is still what it was a moment ago —
  // a job placed through `/schedule`, which the API accepts on a job with one
  // visit — so it keeps the job's id and `visitId: null`, and its next move
  // goes the same way. Keeping the id also keeps the card's React key, and so
  // its focus, across the drop.
  const pending = visit.id.startsWith(PENDING_VISIT_PREFIX);
  return {
    ...job,
    id: pending ? job.id : visit.id,
    jobId: job.id,
    visitId: pending ? null : visit.id,
    visitNumber: index + 1,
    visitCount: job.visits.length,
    scheduledStart: visit.scheduledStart,
    scheduledEnd: visit.scheduledEnd,
    assignedUserId: visit.assignedUserId,
    assignedUserName: visit.assignedUserName,
    emailBounced: visit.emailBounced,
  };
}

/**
 * A job's summary span from its visits — the API's own rule
 * (`JobEndpoints.ProjectExpr`), for an optimistic update that has to leave the
 * job looking the way the server is about to return it: the first visit's
 * start, and the end of the visit that FINISHES last, a point counting as
 * finishing at its start and an interval winning a tie with a point. Returns
 * the visits in start order alongside.
 */
export function withSpan<T extends Pick<Job, 'scheduledStart' | 'scheduledEnd' | 'visits'>>(job: T): T {
  const ms = (iso: string) => new Date(iso).getTime();
  const visits = [...job.visits].sort(
    (a, b) => ms(a.scheduledStart) - ms(b.scheduledStart) || a.id.localeCompare(b.id),
  );
  const last = [...visits].sort(
    (a, b) =>
      ms(b.scheduledEnd ?? b.scheduledStart) - ms(a.scheduledEnd ?? a.scheduledStart) ||
      Number(a.scheduledEnd === null) - Number(b.scheduledEnd === null),
  )[0];
  return {
    ...job,
    visits,
    scheduledStart: visits[0]?.scheduledStart ?? null,
    scheduledEnd: last?.scheduledEnd ?? null,
  };
}
